import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'

const QueryClientContext = createContext(null)

const serializeKey = (key) => JSON.stringify(key)
const parseKey = (str) => JSON.parse(str)

const matchesPartialKey = (stored, partial) => {
  if (!partial) return true
  const target = Array.isArray(partial) ? partial : [partial]
  for (let i = 0; i < target.length; i += 1) {
    if (i >= stored.length) return false
    const expected = target[i]
    const value = stored[i]
    if (typeof expected === 'object' && expected !== null) {
      if (typeof value !== 'object' || value === null) return false
      if (serializeKey(value) !== serializeKey(expected)) return false
    } else if (value !== expected) {
      return false
    }
  }
  return true
}

export const keepPreviousData = Symbol('keepPreviousData')

export class QueryClient {
  constructor(options = {}) {
    this.options = options
    this.queries = new Map()
  }

  clear() {
    this.queries.clear()
  }

  ensureEntry(queryKey) {
    const keyStr = serializeKey(queryKey)
    let entry = this.queries.get(keyStr)
    if (!entry) {
      entry = {
        queryKey,
        data: undefined,
        error: null,
        promise: null,
        listeners: new Set(),
        queryFn: null,
      }
      this.queries.set(keyStr, entry)
    } else {
      entry.queryKey = queryKey
    }
    return entry
  }

  getQueryEntry(queryKey) {
    return this.queries.get(serializeKey(queryKey)) || null
  }

  getQueryData(queryKey) {
    const entry = this.getQueryEntry(queryKey)
    return entry ? entry.data : undefined
  }

  subscribe(queryKey, listener) {
    const entry = this.ensureEntry(queryKey)
    entry.listeners.add(listener)
    return () => {
      entry.listeners.delete(listener)
    }
  }

  notify(queryKey) {
    const entry = this.getQueryEntry(queryKey)
    if (!entry) return
    const snapshot = {
      data: entry.data,
      error: entry.error,
      promise: entry.promise,
    }
    entry.listeners.forEach(listener => listener(snapshot))
  }

  async fetchQuery({queryKey, queryFn}) {
    const keyStr = serializeKey(queryKey)
    const entry = this.ensureEntry(queryKey)
    entry.queryFn = queryFn
    if (entry.promise) {
      return entry.promise
    }
    const promise = Promise.resolve()
      .then(() => queryFn())
      .then(data => {
        const current = this.queries.get(keyStr)
        if (!current) return data
        current.data = data
        current.error = null
        current.promise = null
        this.notify(queryKey)
        return data
      })
      .catch(error => {
        const current = this.queries.get(keyStr)
        if (!current) throw error
        current.error = error
        current.promise = null
        this.notify(queryKey)
        throw error
      })
    entry.promise = promise
    this.notify(queryKey)
    return promise
  }

  cancelQueries({queryKey} = {}) {
    for (const [keyStr, entry] of this.queries.entries()) {
      const parsed = parseKey(keyStr)
      if (!queryKey || matchesPartialKey(parsed, queryKey)) {
        entry.promise = null
        this.notify(entry.queryKey)
      }
    }

    return Promise.resolve()
  }

  invalidateQueries({queryKey} = {}) {
    for (const [keyStr, entry] of this.queries.entries()) {
      const parsed = parseKey(keyStr)
      if (!queryKey || matchesPartialKey(parsed, queryKey)) {
        if (entry.queryFn) {
          this.fetchQuery({queryKey: entry.queryKey, queryFn: entry.queryFn}).catch(() => {})
        }
      }
    }
  }
}

export function QueryClientProvider({client, children}) {
  const value = useMemo(() => client, [client])
  return (
    <QueryClientContext.Provider value={value}>
      {children}
    </QueryClientContext.Provider>
  )
}

export function useQueryClient() {
  const client = useContext(QueryClientContext)
  if (!client) {
    throw new Error('useQueryClient must be used within a QueryClientProvider')
  }
  return client
}

export function useQuery({queryKey, queryFn, placeholderData}) {
  const client = useQueryClient()
  const keyString = useMemo(() => serializeKey(queryKey), [queryKey])
  const [state, setState] = useState(() => {
    const entry = client.getQueryEntry(queryKey)
    if (entry && entry.data !== undefined) {
      return {
        data: entry.data,
        error: entry.error ?? null,
        status: entry.error ? 'error' : 'success',
      }
    }
    if (placeholderData && placeholderData !== keepPreviousData) {
      const placeholder = typeof placeholderData === 'function' ? placeholderData() : placeholderData
      return {
        data: placeholder ?? null,
        error: null,
        status: placeholder != null ? 'success' : 'idle',
      }
    }
    return {
      data: null,
      error: null,
      status: 'pending',
    }
  })
  const previousDataRef = useRef(state.data)

  useEffect(() => {
    previousDataRef.current = state.data
  }, [state.data])

  useEffect(() => {
    let isMounted = true
    const unsubscribe = client.subscribe(queryKey, snapshot => {
      if (!isMounted) return
      setState(prev => {
        const nextData = snapshot.data !== undefined ? snapshot.data : prev.data
        const nextStatus = snapshot.promise
          ? 'pending'
          : snapshot.error
            ? 'error'
            : snapshot.data !== undefined
              ? 'success'
              : prev.status
        return {
          data: nextData,
          error: snapshot.error ?? null,
          status: nextStatus,
        }
      })
    })

    const entry = client.ensureEntry(queryKey)
    entry.queryFn = queryFn
    if (entry.data === undefined && !entry.promise) {
      client.fetchQuery({queryKey, queryFn}).catch(() => {})
    }

    return () => {
      isMounted = false
      unsubscribe()
    }
  }, [client, keyString, queryFn, queryKey])

  useEffect(() => {
    if (placeholderData === keepPreviousData) {
      setState(prev => ({
        ...prev,
        data: prev.data ?? previousDataRef.current,
      }))
    }
  }, [placeholderData, keyString])

  const refetch = useCallback(() => client.fetchQuery({queryKey, queryFn}), [client, queryKey, queryFn])

  const entry = client.getQueryEntry(queryKey)
  const isFetching = !!(entry && entry.promise)
  const isLoading = state.status === 'pending' && !state.data

  return {
    data: state.data,
    error: state.error,
    isError: state.status === 'error',
    isLoading,
    isFetching,
    refetch,
  }
}
